"use client";

import { c as _c } from "react/compiler-runtime";
import { ModalProvider } from "@dc/ui/components";
import { HeroUIProvider } from "@heroui/react";
import { ToastProvider } from "@heroui/toast";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { useRouter } from "next/navigation";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { getQueryClient } from "@/lib/getQueryClient";
interface PublicProvidersProps {
  children: ReactNode;
  /**
   * Whether to include ToastProvider. Defaults to true.
   * Set to false for modal routes to prevent duplicate toasts,
   * since @heroui/toast uses a global queue that all ToastProvider instances render.
   */
  withToast?: boolean;
}

/**
 * PublicProviders - Providers safe for unauthenticated pages
 *
 * These providers do not require authentication and can be used
 * on public pages like login, register, etc.
 *
 * Includes:
 * - Theme management
 * - UI component library
 * - Toast notifications
 * - React Query (for data fetching)
 * - Modal system
 */
export function PublicProviders(t0) {
  const $ = _c(16);
  const {
    children,
    withToast: t1
  } = t0;
  const withToast = t1 === undefined ? true : t1;
  const router = useRouter();
  const [queryClient] = useState(_temp);
  const [showReactQueryDevtools, setShowReactQueryDevtools] = useState(false);
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const reactQueryDevToolsEnabled = localStorage.getItem("react-query-dev-tools") === "true";
      setShowReactQueryDevtools(reactQueryDevToolsEnabled);
    };
    t3 = [];
    $[0] = t2;
    $[1] = t3;
  } else {
    t2 = $[0];
    t3 = $[1];
  }
  useEffect(t2, t3);
  let t4;
  if ($[2] !== withToast) {
    t4 = withToast && <ToastProvider placement="bottom-center" toastOffset={32} />;
    $[2] = withToast;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== showReactQueryDevtools) {
    t5 = showReactQueryDevtools && <ReactQueryDevtools initialIsOpen={false} />;
    $[4] = showReactQueryDevtools;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== children) {
    t6 = <ModalProvider>{children}</ModalProvider>;
    $[6] = children;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== queryClient || $[9] !== t5 || $[10] !== t6) {
    t7 = <QueryClientProvider client={queryClient}>{t5}{t6}</QueryClientProvider>;
    $[8] = queryClient;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== router.push || $[13] !== t4 || $[14] !== t7) {
    t8 = <HeroUIProvider navigate={router.push}>{t4}{t7}</HeroUIProvider>;
    $[12] = router.push;
    $[13] = t4;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  return t8;
}
function _temp() {
  return getQueryClient() || new QueryClient();
}